import { createNested } from 'dreamstate';

import { AnyObject } from '@vimeo/core/types/generic';

import { firebaseConfig } from '../../../configs/FirebaseConfig';
import { liveApplicationConfig } from '../../../configs/LiveApplicationConfig';
import { IFirebaseDependencies, TFirebaseApp, TFirebaseSnapshot } from '../../../types/firebase';
import { IFirebaseInteractionCredentials } from '../../../types/interaction_tools';
import {
  createFirebaseApplication,
  generateFirebaseApplicationName,
} from '../../../utils/firebase/management';
import { ELiveErrorCategory, trackLiveAction, trackLiveError } from '../../../utils/tracking';
import {
  ELiveRealtimeSignal,
  ELiveSignal,
  TComposerSessionReadySignalEvent,
  TFirebaseInteractionAppReadySignalEvent,
  TInteractionSessionReadySignalEvent,
} from '../../signals';
import { AbstractFirebaseManager } from './AbstractFirebaseManager';

export async function tryToSetupInteractionConnection<T>(
  manager: AbstractFirebaseManager<T>,
): Promise<void> {
  const { interactionCredentials } = manager.context;

  if (manager.firebase && interactionCredentials && !manager.interactionApp) {
    await createInteractionConnection(manager, manager.firebase, interactionCredentials);
  }
}

/**
 * Create interaction firebase application and subscribe to connection/auth statuses.
 */
export async function createInteractionConnection<T>(
  manager: AbstractFirebaseManager<T>,
  firebase: IFirebaseDependencies,
  credentials: IFirebaseInteractionCredentials,
): Promise<void> {
  const { config, roomId, token, appName } = credentials;
  const metadata: AnyObject = { roomId, appName };

  manager.log.info('Setting up interaction firebase connections:', metadata);
  trackLiveAction('fb_interaction_connection_create');

  try {
    let interactionAppReadyNotified: boolean = false;

    const [app, unSubscribers] = await createFirebaseApplication(
      firebase,
      {
        config,
        name: appName,
        token,
        persistence: firebaseConfig.AUTH.DEFAULT_PERSISTENCE_TYPE,
      },
      {
        onPersistenceChangeFinished: (persistence) => {
          manager.log.info('Interaction auth persistence state set to:', persistence);
        },
        onPersistenceChangeFailed: (error, persistence) => {
          manager.log.error(
            'Interaction auth persistence state change failed:',
            error,
            persistence,
          );
        },
        onBeforeAuthStateChanged: (firebaseUser) => {
          manager.log.info(
            'Received before interaction auth update:',
            metadata,
            Boolean(firebaseUser),
          );

          trackLiveAction('fb_interaction_before_connection_auth_update', {
            isDisposed: manager.IS_DISPOSED,
            name: appName,
            roomId: roomId,
            user: Boolean(firebaseUser),
          });
        },
        onIdTokenChanged: (firebaseUser) => {
          manager.log.info(
            'Received interaction ID token changed event auth update:',
            metadata,
            Boolean(firebaseUser),
          );
        },
        onAuthStateChanged: (firebaseUser) => {
          trackLiveAction('fb_interaction_connection_auth_update', {
            isDisposed: manager.IS_DISPOSED,
            name: appName,
            user: Boolean(firebaseUser),
          });

          if (manager.IS_DISPOSED) {
            return manager.log.info('Received interaction auth update after disposal:', metadata);
          } else {
            manager.log.info('Received interaction auth update:', metadata);
          }

          manager.setContext({
            interactionConnection: manager.context.interactionConnection.asMerged({
              user: firebaseUser,
            }),
          });
        },
        onConnectionStateChanged: (snapshot: TFirebaseSnapshot, application: TFirebaseApp) => {
          if (manager.IS_DISPOSED) {
            return manager.log.info('Received interaction status change after disposal:', metadata);
          }

          const isConnected: boolean = snapshot.val() === true;

          // Track firebase connect disconnect if it is actual event, not first two on init.
          if (interactionAppReadyNotified) {
            trackLiveAction(
              isConnected ? 'fb_interaction_connected' : 'fb_interaction_disconnected',
            );
          }

          manager.log.info('Interaction firebase connection state change:', isConnected, metadata);
          manager.setContext({
            interactionConnection: manager.context.interactionConnection.asMerged({ isConnected }),
          });

          if (isConnected && !interactionAppReadyNotified) {
            manager.log.info('Interaction firebase app is ready:', metadata);
            trackLiveAction('fb_interaction_app_ready');

            interactionAppReadyNotified = true;

            manager.emitSignal<TFirebaseInteractionAppReadySignalEvent['data']>({
              type: ELiveRealtimeSignal.FIREBASE_INTERACTION_APP_READY,
              data: {
                firebase,
                app: application,
                metadata: credentials.metadata,
                user: credentials.user,
                roomHash: credentials.roomHash,
                roomId: credentials.roomId,
              },
            });
          }
        },
        onClockSkewChanged: (snapshot) => {
          liveApplicationConfig.TIMING.CLOCK_SKEW = snapshot.val() || 0;
        },
      },
    );

    manager.interactionApp = app;

    if (manager.IS_DISPOSED) {
      manager.log.info('Interaction apps de-synced with fast updates, need unsubscribing');
      unSubscribers.forEach((it) => it());
    } else {
      manager.log.info('Interaction apps created:', metadata);
      manager.addUnSubscribers(unSubscribers, 'interaction');
    }
  } catch (error) {
    manager.log.error('Failed to authorize interaction firebase:', error, metadata);
    trackLiveError(error, {
      method: 'createInteractionConnection',
      category: ELiveErrorCategory.FIREBASE,
    });
  }

  trackLiveAction('fb_interaction_connection_created');
}

/**
 * Info loaded, fetch interaction credentials and start firebase for interaction application.
 */
export async function initializeComposerInteractionToolsConnection<T>(
  manager: AbstractFirebaseManager<T>,
  signal: TComposerSessionReadySignalEvent,
): Promise<void> {
  const canUseInteraction: boolean = Boolean(signal.data?.metadata?.connections?.interactionTools);

  if (canUseInteraction) {
    manager.log.info('Session information loaded, fetching interaction credentials:', signal.data);
  } else {
    return manager.log.warn('Cannot load interaction credentials for session, no connection');
  }

  try {
    const {
      firebase: { config, jwt, appName },
      metadata,
      user,
      roomId,
      roomHash,
    } = await manager.loadInteractionConfig(signal);

    manager.setContext({
      interactionCredentials: {
        config: {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          databaseURL: config.databaseUrl,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
        },
        metadata,
        appName: generateFirebaseApplicationName(
          manager.uuid,
          appName ?? `vimeo-interaction-${manager.uuid}`,
        ),
        token: jwt,
        roomHash,
        roomId,
        user,
      },
      isInteractionReady: true,
    });

    manager.log.info(
      'Interaction config credentials ready:',
      manager.context.interactionCredentials,
      config,
    );

    await tryToSetupInteractionConnection(manager);
  } catch (error) {
    manager.log.error('Failed to load interaction FB config:', error);
    manager.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });

    trackLiveError(error, {
      method: 'initializeComposerInteractionToolsConnection',
      category: ELiveErrorCategory.FIREBASE,
    });
  }
}

/**
 * Info loaded, fetch interaction credentials and start firebase for interaction application.
 */
export async function initializeInteractionToolsConnection<T>(
  manager: AbstractFirebaseManager<T>,
  signal: TInteractionSessionReadySignalEvent,
): Promise<void> {
  manager.log.info('Session information loaded, initialize interaction:', signal.data);

  try {
    const {
      firebase: { config, jwt, appName },
      user,
      roomId,
      roomHash,
      metadata,
    } = signal.data;

    manager.setContext({
      interactionCredentials: {
        config: {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          databaseURL: config.databaseUrl,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
        },
        metadata,
        appName: generateFirebaseApplicationName(
          manager.uuid,
          appName ?? `vimeo-interaction-${manager.uuid}`,
        ),
        token: jwt,
        roomHash,
        roomId: roomId,
        user,
      },
      isInteractionReady: true,
    });

    manager.log.info(
      'Interaction config credentials ready:',
      manager.context.interactionCredentials,
      config,
    );

    await tryToSetupInteractionConnection(manager);
  } catch (error) {
    manager.log.error('Failed to load interaction FB config:', error);
    manager.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });

    trackLiveError(error, {
      method: 'initializeInteractionToolsConnection',
      category: ELiveErrorCategory.FIREBASE,
      data: { signal },
    });
  }
}

/**
 * Dispose connection for interaction application.
 */
export function disposeInteractionToolsConnection<T>(manager: AbstractFirebaseManager<T>): void {
  manager.log.info('Disposing interaction tools connection');

  if (manager.interactionApp) {
    manager.log.info('Disposing interaction tools app:', manager.interactionApp.name);
    manager.interactionApp.name ? manager.disposeSubscribers(manager.interactionApp.name) : void 0;
    manager.firebase?.firebase.deleteApp(manager.interactionApp);
    manager.interactionApp = null;
  }

  manager.setContext({
    isInteractionReady: false,
    interactionCredentials: null,
    interactionConnection: createNested({
      isConnected: false,
      user: null,
    }),
  });
}
